"use client";

import React from "react";
import { CircleCheck, ExternalLink, FileCheck, RefreshCw, Scale, ShieldCheck } from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
type Etape = {
  titre: string;
  items: string[];
};
type Service = {
  title: string;
  icon: LucideIcon;
  objective: string;
  deliverables?: string[];
  etapes?: Etape[];
  modalites?: string;
  livrables: string;
  note?: string;
  lien?: {
    label: string;
    href: string;
  };
};
const services: Service[] = [{
  title: "RNQ révisé et loi du 25\u00a0juin 2026\u00a0: mettre à jour votre système qualité",
  icon: RefreshCw,
  objective: "Mettre vos pratiques et vos documents à jour de la loi n°\u00a02026-534 du 25\u00a0juin 2026 et du décret n°\u00a02026-728 du 1er\u00a0août 2026, avant votre prochain audit.",
  etapes: [{
    titre: "L'analyse",
    items: ["Loi n°\u00a02026-534 du 25\u00a0juin 2026\u00a0: dispositions applicables à votre organisme, mise en conformité de vos pratiques et de vos documents.", "Analyse des évolutions du référentiel et, dès sa publication, du guide de lecture, appliquées à votre situation.", "Identification du niveau de conformité attendu et des éléments de preuve à réunir.", "Repérage des écarts entre ces exigences et vos processus et votre système documentaire.", "Définition des travaux à conduire et mise à disposition de trames."]
  }, {
    titre: "Le suivi",
    items: ["Revue des documents que vous produisez.", "Ajustements à partir de cette revue.", "Réponses à vos questions tout au long de la mise en œuvre."]
  }],
  modalites: "En plusieurs séances, d'une durée adaptée à votre organisme et à son activité.",
  livrables: "Analyse des écarts, plan des travaux à conduire, trames, retours sur les documents produits."
}, {
  title: "Audit initial\u00a0: obtenir votre première certification Qualiopi®",
  icon: Scale,
  objective: "Préparer votre organisme à son premier audit Qualiopi®.",
  deliverables: ["Diagnostic de conformité\u00a0: analyse de vos pratiques actuelles au regard des 33\u00a0indicateurs du Référentiel National Qualité, dans leur rédaction issue du décret n°\u00a02026-728 du 1er\u00a0août 2026, identification des écarts et des points de vigilance.", "Vulgarisation du Référentiel\u00a0: explication de chaque critère et indicateur dans un langage clair et accessible à toutes vos équipes (direction, formateurs, administratifs).", "Co-construction des outils\u00a0: conception ou amélioration de vos processus, procédures et outils de traçabilité (accueil des stagiaires, évaluation de la satisfaction, suivi des compétences des formateurs, veille réglementaire, etc.).", "Appropriation par les équipes\u00a0: sensibilisation de vos collaborateurs aux exigences Qualiopi®, pour que chacun comprenne son rôle et applique les bonnes pratiques.", "Préparation à l'audit\u00a0: simulation d'audit à blanc, préparation des preuves documentaires…"],
  livrables: "Plan d'actions de mise en conformité, outils de traçabilité, procédures formalisées."
}, {
  title: "Audit de surveillance\u00a0: maintenir votre certification",
  icon: FileCheck,
  objective: "Préparer votre audit de surveillance (à mi-parcours du cycle de certification) et démontrer la pérennité de votre système qualité.",
  deliverables: ["Diagnostic de maintien\u00a0: vérification du maintien de vos pratiques qualité depuis l'audit initial, identification des éventuelles dérives ou points de fragilité.", "Actualisation des outils\u00a0: mise à jour de vos processus et procédures en fonction des évolutions.", "Accompagnement à l'amélioration continue\u00a0: conseil sur les actions d'amélioration à mettre en œuvre pour renforcer votre système qualité.", "Préparation à l'audit\u00a0: revue des preuves documentaires, simulation d'audit à blanc."],
  livrables: "Rapport de diagnostic, outils actualisés, plan d'amélioration continue."
}, {
  title: "Audit de renouvellement\u00a0: prolonger votre certification",
  icon: ShieldCheck,
  objective: "Préparer votre audit de renouvellement (avant l'échéance du cycle de trois ans) en vue d'une nouvelle certification de trois ans.",
  deliverables: ["Diagnostic approfondi\u00a0: analyse complète de votre système qualité sur les trois dernières années, identification des forces et des axes d'amélioration.", "Valorisation de votre démarche qualité\u00a0: préparation des éléments démontrant votre engagement dans l'amélioration continue (actions correctives, évolutions des pratiques, résultats obtenus).", "Actualisation des outils\u00a0: mise à jour de vos processus et procédures en fonction des nouvelles activités ou des retours d'expérience.", "Préparation à l'audit\u00a0: revue complète des preuves documentaires, simulation d'audit à blanc."],
  livrables: "Rapport de diagnostic, outils actualisés, dossier de preuves structuré."
}];
function ItemList({
  items
}: {
  items: string[];
}) {
  return <ul className="space-y-4">
      {items.map((item, i) => <li key={i} data-index={i} className="flex items-start gap-3">
          <CircleCheck className="w-5 h-5 text-primary shrink-0 mt-1" />
          <span className="text-base leading-relaxed !text-[hsl(210,10%,23%)]">
            {item}
          </span>
        </li>)}
    </ul>;
}
function ServiceCard({
  service,
  index
}: {
  service: Service;
  index: number;
}) {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <div ref={ref} data-index={index} className={`transition-all duration-700 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`} style={{
    transitionDelay: `${index * 150}ms`
  }}>
      <Card className="overflow-hidden rounded-xl border border-[#d6cfc4]/60 bg-[#F5F0EB] shadow-sm hover:shadow-lg hover:-translate-y-1 transition-all duration-300">
        <CardContent className="p-8 lg:p-10">
          {/* Titre + icône */}
          <div className="flex items-center gap-4 mb-6">
            <div className="flex items-center justify-center w-14 h-14 rounded-xl bg-primary/10 text-primary shrink-0">
              <service.icon className="w-7 h-7" />
            </div>
            <h3 className="font-serif text-2xl md:text-3xl font-bold text-foreground">
              {service.title}
            </h3>
          </div>

          {/* Objectif */}
          <div className="mb-6">
            <p className="text-lg font-bold text-gray-800 mb-1">Objectif</p>
            <p className="text-base leading-relaxed !text-[hsl(210,10%,23%)]">
              {service.objective}
            </p>
          </div>

          {/* L'accompagnement : liste simple ou étapes */}
          {service.etapes ? service.etapes.map((etape, e) => <div key={e} className="mb-6">
                <p className="text-lg font-bold text-gray-800 mb-3">
                  {etape.titre}
                </p>
                <ItemList items={etape.items} />
              </div>) : service.deliverables && <div className="mb-6">
                <p className="text-lg font-bold text-gray-800 mb-3">
                  L'accompagnement
                </p>
                <ItemList items={service.deliverables} />
              </div>}

          {/* Modalités */}
          {service.modalites && <div className="mb-6">
              <p className="text-lg font-bold text-gray-800 mb-1">Modalités</p>
              <p className="text-base leading-relaxed !text-[hsl(210,10%,23%)]">
                {service.modalites}
              </p>
            </div>}

          {/* Livrables */}
          <div>
            <p className="text-lg font-bold text-gray-800 mb-1">Livrables</p>
            <p className="text-base leading-relaxed !text-[hsl(210,10%,23%)] font-semibold">
              {service.livrables}
            </p>
            {service.lien && <a href={service.lien.href} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 mt-4 text-base font-semibold text-[hsl(343,80%,35%)] hover:underline underline-offset-4">
                {service.lien.label}
                <ExternalLink className="size-4" aria-hidden="true" />
              </a>}
            {service.note && <p className="mt-4 text-sm italic leading-relaxed !text-[hsl(210,10%,23%)]/80">
                {service.note}
              </p>}
          </div>
        </CardContent>
      </Card>
    </div>;
}
export default function LesAccompagnements() {
  const {
    ref: headerRef,
    isVisible: isHeaderVisible
  } = useRevealOnScroll({
    threshold: 0.2
  });
  return <section id="les-accompagnements" className="bg-background relative overflow-hidden ![padding:60px_0px_128px_0px]">
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none opacity-30">
        <div className="absolute top-[-10%] right-[-5%] w-[500px] h-[500px] rounded-full bg-primary/5 blur-[100px]" />
        <div className="absolute bottom-[-10%] left-[-5%] w-[600px] h-[600px] rounded-full bg-accent/10 blur-[120px]" />
      </div>

      <div className="container max-w-7xl mx-auto px-6 relative z-10 !text-[hsl(210,10%,23%)]">
        <div ref={headerRef} className={`max-w-3xl mb-16 transition-all duration-700 ease-out ${isHeaderVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <h2 className="font-serif text-4xl font-bold mb-6 !text-[hsl(210,10%,23%)] lg:text-5xl">
            Les accompagnements Qualiopi®
          </h2>
          <p className="text-xl leading-relaxed mb-6 !text-[hsl(210,10%,23%)] md:text-2xl">
            Des accompagnements sur mesure, adaptés à votre situation
          </p>
          <p className="text-lg leading-relaxed !text-[hsl(210,10%,23%)]">
            Effet Conseil adapte l'accompagnement à votre contexte&nbsp;: mise à
            jour au RNQ révisé, audit initial pour une première certification,
            audit de surveillance pour maintenir votre certification ou audit de
            renouvellement pour la prolonger de trois ans.
          </p>
        </div>

        <div className="flex flex-col gap-8">
          {services.map((service, index) => <ServiceCard key={index} index={index} service={service} />)}
        </div>
      </div>
    </section>;
}